import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram } from 'lucide-react';
import logo from '../assets/logo.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-br from-crimson-600 via-crimson-700 to-crimson-800 text-cream-100 overflow-hidden">
      {/* Decorative top border */}
      <div className="h-1.5 bg-gradient-to-r from-gold-300 via-gold-500 to-gold-300"></div>

      {/* Ambient glow */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gold-300 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Company Info */}
          <div className="space-y-5 lg:col-span-1">
            <div className="flex items-center space-x-3">
              <div className="bg-white rounded-lg border border-gold-200/60 px-2.5 py-1.5">
                <img
                  src={logo}
                  alt="India Glass Works And Frames logo"
                  className="h-9 w-auto object-contain"
                />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg tracking-wide">
                  India Glass Works
                </h3>
                <p className="text-xs text-gold-200">
                  &amp; Frames, Pune
                </p>
              </div>
            </div>
            <p className="text-cream-200/90 text-sm leading-relaxed">
              Dealers in all types of glass, mirrors, stained glass, etching and aluminium sliding — plus expert picture framing, since generations.
            </p>
            <a
              href="https://www.instagram.com/explore/tags/indiaglassworks/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gold-200 hover:text-white text-sm transition-colors"
            >
              <Instagram size={16} /> Follow our work
            </a>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="font-display font-bold text-lg mb-6 text-gold-200">
              Quick Links
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-cream-200/90 hover:text-white transition-all duration-300">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-cream-200/90 hover:text-white transition-all duration-300">
                  Our Products
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-cream-200/90 hover:text-white transition-all duration-300">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="font-display font-bold text-lg mb-6 text-gold-200">
              Get In Touch
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3 p-4 rounded-lg bg-black/10 hover:bg-black/20 transition-all duration-300">
                <MapPin size={20} className="text-gold-300 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gold-200 text-xs mb-1 font-semibold uppercase tracking-wide">Location</p>
                  <span className="text-cream-200/90 text-sm">
                    391, Kolsa Lane, Off M. G. Road,<br />Camp, Pune - 411001
                  </span>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 rounded-lg bg-black/10 hover:bg-black/20 transition-all duration-300">
                <Phone size={20} className="text-gold-300 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gold-200 text-xs mb-1 font-semibold uppercase tracking-wide">Phone</p>
                  <div className="text-cream-200/90 text-sm space-y-1">
                    <div className="hover:text-white transition-colors">
                      Huzefa: <a href="tel:+919359370652">+91 9359370652</a>
                    </div>
                    <div className="hover:text-white transition-colors">
                      Taher: <a href="tel:+919890097573">+91 9890097573</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sm:col-span-2 flex items-start space-x-3 p-4 rounded-lg bg-black/10 hover:bg-black/20 transition-all duration-300">
                <Mail size={20} className="text-gold-300 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gold-200 text-xs mb-1 font-semibold uppercase tracking-wide">Email</p>
                  <a
                    href="mailto:indiaglassworks@gmail.com"
                    className="text-cream-200/90 hover:text-white transition-colors text-sm"
                  >
                    indiaglassworks@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/15 mt-12 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-cream-200/70 text-sm text-center sm:text-left">
              &copy; {currentYear} India Glass Works And Frames. All rights reserved.
            </p>
            <p className="text-cream-200/70 text-sm">
              Camp, Pune, Maharashtra
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
